// Hukuki metinler. Yazılanlar sistemin bugünkü davranışını anlatır: veri toplama, saklama ya da
// e-posta davranışı değişirse bu metinler ve shared/plans.ts içindeki TERMS_VERSION da güncellenmelidir.
import type { ReactNode } from 'react';
import { LuArrowLeft } from 'react-icons/lu';
import { LEGAL_CONTACT, TERMS_EFFECTIVE_LABEL } from '../../shared/plans';
import { NovaMark } from '../components/ui';

export type LegalKind = 'gizlilik' | 'kvkk' | 'kullanim-kosullari';

const Mail = () => <a href={`mailto:${LEGAL_CONTACT}`}>{LEGAL_CONTACT}</a>;

const DATA_LIST = (
  <ul>
    <li>
      <strong>Öğretmen hesabı:</strong> ad soyad, e-posta adresi, şifrenin geri döndürülemez özeti, plan ve abonelik bilgisi, koşulların kabul tarihi, son giriş zamanı. Paket talebi gönderirseniz yazdığınız telefon, kurum, öğrenci sayısı ve mesaj.
    </li>
    <li>
      <strong>Ders içeriği:</strong> öğretmenin yüklediği PDF dosyaları, ders başlığı, derste yapılan çizim ve yazılar.
    </li>
    <li>
      <strong>Derse katılanlar:</strong> katılımcının yazdığı ad, derse giriş ve çıkış zamanları, derste kalma süresi, çizim izni. Bu süreler dersin öğretmenine raporlanır. Öğrenciler hesap açmaz; e-posta adresleri istenmez.
    </li>
    <li>
      <strong>Teknik veriler:</strong> oturum çerezi ve hatalı giriş denemelerini sınırlamak için IP adresi. IP adresi veritabanına kaydedilmez, sunucu belleğinde geçici olarak tutulur.
    </li>
  </ul>
);

const RETENTION = (
  <ul>
    <li>Dersler, çizimleri, katılımcı listesi ve PDF’leriyle birlikte, son etkinlikten sonra hesabın planındaki süre (deneme planında 14 gün, standart planda 90 gün) dolunca otomatik silinir. Öğretmen dersi istediği zaman kendisi de silebilir.</li>
    <li>E-postası doğrulanmayan hesaplar 7 gün sonra silinir.</li>
    <li>Diğer hesap bilgileri, hesap silinene kadar saklanır.</li>
    <li>Yedekler 14 gün sonra silinir; silinen veriler bu süre içinde yedeklerde kalabilir.</li>
  </ul>
);

const RIGHTS = (
  <>
    <p>KVKK’nın 11. maddesi uyarınca kişisel verilerinizin işlenip işlenmediğini öğrenme, bilgi talep etme, işlenme amacını ve aktarıldığı kişileri öğrenme, eksik veya yanlış verilerin düzeltilmesini, silinmesini veya yok edilmesini isteme, otomatik analiz sonucu aleyhinize çıkan sonuca itiraz etme ve zarara uğramanız hâlinde giderim talep etme haklarına sahipsiniz.</p>
    <p>
      Hesabınızın ve verilerinizin silinmesi dâhil taleplerinizi <Mail /> adresine iletebilirsiniz.
    </p>
  </>
);

const DOCS: Record<LegalKind, { title: string; body: ReactNode }> = {
  gizlilik: {
    title: 'Gizlilik Politikası',
    body: (
      <>
        <p>Bu politika, Supernova Academy tarafından sunulan Nova Class hizmetinde hangi verilerin nasıl kullanıldığını açıklar.</p>
        <h2>Topladığımız veriler</h2>
        {DATA_LIST}
        <h2>Verileri ne için kullanıyoruz?</h2>
        <ul>
          <li>Hesabınızı açmak, giriş yapmanızı sağlamak ve canlı dersi yürütmek.</li>
          <li>Doğrulama, şifre sıfırlama, hoş geldin ve paket talebiyle ilgili e-postalar göndermek; paket talebinde sizinle iletişime geçmek. Tanıtım e-postası göndermiyoruz.</li>
          <li>Plan sınırlarını uygulamak, hizmetin güvenliğini sağlamak ve kötüye kullanımı önlemek.</li>
        </ul>
        <h2>Paylaşım</h2>
        <p>Verilerinizi satmıyoruz ve reklam amacıyla kullanmıyoruz. Bir dersteki çizimleri ve katılımcı adlarını o derse katılanlar görür. Hizmeti sunmak için barındırma ve e-posta hizmeti aldığımız sağlayıcıların sunucularından yararlanırız. Yasal zorunluluk hâlinde yetkili kurumlarla paylaşım yapılabilir.</p>
        <h2>Çerezler ve tarayıcı depolama</h2>
        <p>Yalnızca girişinizi hatırlayan bir oturum çerezi kullanırız (30 gün). Araç tercihlerinizi ve derse girdiğiniz adı tarayıcınızda saklarız. Reklam veya analiz çerezi kullanmıyoruz.</p>
        <h2>Güvenlik</h2>
        <p>Bağlantılar HTTPS ile şifrelenir, şifreler geri döndürülemez biçimde saklanır, yedekler düzenli alınır. Hiçbir sistem kusursuz değildir; bir sorun fark ederseniz lütfen bize bildirin.</p>
        <h2>Saklama süreleri</h2>
        {RETENTION}
        <h2>Haklarınız</h2>
        {RIGHTS}
      </>
    ),
  },
  kvkk: {
    title: 'KVKK Aydınlatma Metni',
    body: (
      <>
        <p>6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) uyarınca, Nova Class hizmeti kapsamında işlenen kişisel verileriniz hakkında sizi bilgilendiriyoruz.</p>
        <h2>Veri sorumlusu</h2>
        <p>
          Supernova Academy. İletişim: <Mail />
        </p>
        <h2>İşlenen kişisel veriler</h2>
        {DATA_LIST}
        <h2>İşleme amaçları</h2>
        <ul>
          <li>Üyelik işlemlerinin yürütülmesi ve e-posta adresinin doğrulanması,</li>
          <li>Canlı ders hizmetinin sunulması, katılımcıların derste görüntülenmesi ve öğretmene ders süresi raporu verilmesi,</li>
          <li>Bilgi güvenliğinin sağlanması ve kötüye kullanımın önlenmesi,</li>
          <li>Hizmetle ilgili zorunlu bildirimlerin yapılması ve paket taleplerinin yanıtlanması.</li>
        </ul>
        <h2>Hukuki sebep ve toplama yöntemi</h2>
        <p>Kişisel verileriniz; kayıt ve katılım formları ile hizmeti kullanmanız sırasında elektronik ortamda toplanır. KVKK’nın 5/2 maddesindeki bir sözleşmenin kurulması veya ifası için gerekli olması, hukuki yükümlülüklerin yerine getirilmesi ve temel hak ve özgürlüklerinize zarar vermemek kaydıyla meşru menfaatlerimiz için zorunlu olması hukuki sebeplerine dayanılarak işlenir.</p>
        <h2>Aktarım</h2>
        <p>Kişisel verileriniz, hizmetin sunulması amacıyla barındırma ve e-posta hizmeti aldığımız sağlayıcılara ve yasal zorunluluk hâlinde yetkili kamu kurumlarına aktarılabilir.</p>
        <h2>Saklama süreleri</h2>
        {RETENTION}
        <h2>Haklarınız</h2>
        {RIGHTS}
      </>
    ),
  },
  'kullanim-kosullari': {
    title: 'Kullanım Koşulları',
    body: (
      <>
        <p>Nova Class’ı kullanarak bu koşulları kabul etmiş olursunuz.</p>
        <h2>Hizmet</h2>
        <p>Nova Class, öğretmenlerin PDF üzerinde canlı ders anlatmasını sağlayan bir hizmettir. Hizmet geliştirilmeye devam etmektedir; özellikler ve plan sınırları değişebilir.</p>
        <h2>Hesabınız</h2>
        <ul>
          <li>Doğru bilgi vermek ve şifrenizi gizli tutmak sizin sorumluluğunuzdadır.</li>
          <li>Öğretmen bağlantısı dersi yönetme yetkisi verir; kimseyle paylaşmayın.</li>
          <li>Koşulları ihlal eden veya hizmete zarar veren hesaplar askıya alınabilir.</li>
        </ul>
        <h2>İçerik ve sorumluluk</h2>
        <ul>
          <li>Yüklediğiniz PDF’lerin ve derste paylaştıklarınızın sorumluluğu size aittir; bunları kullanma hakkına sahip olmalısınız.</li>
          <li>Öğrenci bağlantısını bilen herkes derse katılabilir; bağlantıyı yalnızca öğrencilerinizle paylaşın.</li>
          <li>Reşit olmayan öğrencilerle yapılan derslerde gerekli izinleri almak öğretmenin ya da bağlı olduğu kurumun sorumluluğundadır.</li>
        </ul>
        <h2>Saklama ve kesintiler</h2>
        <p>Dersler, planınızdaki süre dolunca otomatik silinir; önemli notlarınızı PDF olarak indirin. Kesintisiz hizmet için çalışırız ancak kesinti veya veri kaybı olmayacağını garanti edemeyiz.</p>
        <h2>Değişiklikler ve iletişim</h2>
        <p>
          Koşullar güncellenebilir; önemli değişiklikleri duyururuz. Sorularınız için: <Mail />
        </p>
      </>
    ),
  },
};

const OTHERS: { kind: LegalKind; label: string }[] = [
  { kind: 'gizlilik', label: 'Gizlilik Politikası' },
  { kind: 'kvkk', label: 'KVKK Aydınlatma Metni' },
  { kind: 'kullanim-kosullari', label: 'Kullanım Koşulları' },
];

export function Legal({ kind }: { kind: LegalKind }) {
  const doc = DOCS[kind];
  return (
    <div className="legal">
      <header className="legal-header">
        <a className="brand" href="/">
          <NovaMark size={32} />
          <span className="brand-name">Nova Class</span>
        </a>
        <a className="auth-back legal-back" href="/">
          <LuArrowLeft /> Ana sayfa
        </a>
      </header>
      <main className="legal-body">
        <h1>{doc.title}</h1>
        <p className="muted small">Yürürlük tarihi: {TERMS_EFFECTIVE_LABEL}</p>
        {doc.body}
        <nav className="legal-nav" aria-label="Diğer metinler">
          {OTHERS.filter((o) => o.kind !== kind).map((o) => (
            <a key={o.kind} href={`/${o.kind}`}>
              {o.label}
            </a>
          ))}
        </nav>
      </main>
    </div>
  );
}
